import { queryKeys } from '@/api/queryKeys';
import {
  addStockMutation,
  updateStockMutation,
} from '@/api/stock/stockMutations';
import { getStockQuery } from '@/api/stock/stockQueries';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common/Table';
import { Tooltip } from '@/components/common/Tooltip';
import type {
  StockBatchRow,
  StockEntry,
  StockFilter,
  StockItem,
  StockTableRow,
} from '@/domain/Stock';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/utils/dateUtils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { Fragment, useCallback, useState } from 'react';
import { toast } from 'sonner';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import { StockEditDialog } from './StockEditDialog';
import { StockFilters } from './StockFilters';
import type { AddStockFormData } from './StockFormDialog';
import { StockFormDialog } from './StockFormDialog';

const MAX_DOSE_LENGTH = 15;

const truncateDose = (dose: string): string => {
  if (dose.length > MAX_DOSE_LENGTH) {
    return `${dose.slice(0, MAX_DOSE_LENGTH)}...`;
  }
  return dose;
};

const isTruncated = (dose: string): boolean => dose.length > MAX_DOSE_LENGTH;

const getTemplateStockId = (
  variation: StockItem['stock_info']['variations'][0],
): number | undefined =>
  variation.stocks[variation.stocks.length - 1]?.stock_id;

const toBatchRow = (
  item: StockItem,
  variation: StockItem['stock_info']['variations'][0],
  stock: StockEntry,
): StockBatchRow => ({
  stockId: stock.stock_id,
  supplementName: item.name,
  strength: variation.strength,
  lotNumber: stock.lot_number ?? null,
  initialQuantity: stock.order_count,
  currentQuantity: stock.remaining,
  expireDate: stock.expire_date ?? null,
});

export const Stock = ({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<StockFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState<StockTableRow | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<StockBatchRow | null>(
    null,
  );
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [expandedVariations, setExpandedVariations] = useState<Set<string>>(
    new Set(),
  );

  const handleAccordionOpen = useCallback(() => {
    setFilter('all');
    setSearch('');
    setPage(1);
  }, []);

  const { data: response, isLoading } = useQuery(
    getStockQuery(page, filter, search || undefined),
  );

  const stockItems = response?.data || [];
  const meta = response?.meta;

  const addMutation = useMutation({
    ...addStockMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.stock.index],
      });
      setFormDialogOpen(false);
      setSelectedRow(null);
      toast.success('Stock added successfully');
    },
  });

  const updateMutation = useMutation({
    ...updateStockMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.stock.index],
      });
      setEditDialogOpen(false);
      setSelectedBatch(null);
      toast.success('Stock batch updated successfully');
    },
  });

  const toggleItemExpand = (itemId: number) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleVariationExpand = (key: string) => {
    setExpandedVariations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleAddClick = (
    e: React.MouseEvent,
    item: StockItem,
    variation: StockItem['stock_info']['variations'][0],
  ) => {
    e.stopPropagation();
    const stockId = getTemplateStockId(variation);
    if (!stockId) return;

    setSelectedRow({
      supplementId: item.id,
      supplementName: item.name,
      strengthId: variation.strength_id,
      strength: variation.strength,
      stockId,
      orderQuantity: variation.order_count,
      stockQuantity: variation.remaining,
      stockKg: variation.total_weight_kg,
      lowStock: variation.low_stock,
      outOfStock: variation.out_of_stock,
    });
    setFormDialogOpen(true);
  };

  const handleEditClick = (
    e: React.MouseEvent,
    item: StockItem,
    variation: StockItem['stock_info']['variations'][0],
    stock: StockEntry,
  ) => {
    e.stopPropagation();
    setSelectedBatch(toBatchRow(item, variation, stock));
    setEditDialogOpen(true);
  };

  const handleFormSubmit = (id: number, data: AddStockFormData) => {
    addMutation.mutate({ id, ...data });
  };

  const handleEditSubmit = (
    id: number,
    data: { expire_date: string; lot_number: string },
  ) => {
    updateMutation.mutate({ id, ...data });
  };

  const handleFilterChange = (newFilter: StockFilter) => {
    setFilter(newFilter);
    setPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const renderBatchRows = (
    item: StockItem,
    variation: StockItem['stock_info']['variations'][0],
    variationKey: string,
  ) => {
    if (!expandedVariations.has(variationKey)) return null;

    return variation.stocks.map((stock) => (
      <TableRow
        key={`batch-${stock.stock_id}`}
        className="bg-slate-100 hover:bg-slate-100"
      >
        <TableCell className="w-8" />
        <TableCell className="pl-8 text-sm text-slate-600">
          {stock.lot_number || '-'}
        </TableCell>
        <TableCell className="text-sm text-slate-600">
          {stock.order_count}
        </TableCell>
        <TableCell className="text-sm text-slate-600">
          {stock.remaining}
        </TableCell>
        <TableCell className="text-sm text-slate-600">
          {formatDate(stock.expire_date)}
        </TableCell>
        <TableCell className="text-right">
          <button
            onClick={(e) => handleEditClick(e, item, variation, stock)}
            className="text-primary hover:text-primary/80 text-sm font-medium"
          >
            Edit
          </button>
        </TableCell>
      </TableRow>
    ));
  };

  const renderVariationRow = (
    item: StockItem,
    variation: StockItem['stock_info']['variations'][0],
  ) => {
    const variationKey = `${item.id}-${variation.strength_id}`;
    const isBatchExpanded = expandedVariations.has(variationKey);
    const hasBatches = variation.stocks.length > 0;

    return (
      <Fragment key={variationKey}>
        <TableRow
          className={cn('bg-slate-50', hasBatches && 'cursor-pointer')}
          onClick={() => hasBatches && toggleVariationExpand(variationKey)}
        >
          <TableCell className="w-8">
            {hasBatches && (
              <ChevronDown
                className={cn(
                  'size-4 transition-transform',
                  isBatchExpanded && 'rotate-180',
                )}
              />
            )}
          </TableCell>
          <TableCell className="pl-2">{item.name}</TableCell>
          <TableCell>
            <Tooltip
              content={variation.strength}
              enabled={isTruncated(variation.strength)}
            >
              <span>{truncateDose(variation.strength)}</span>
            </Tooltip>
          </TableCell>
          <TableCell>{variation.order_count}</TableCell>
          <TableCell>{variation.remaining}</TableCell>
          <TableCell>{variation.total_weight_kg}</TableCell>
          <TableCell className="text-right">
            <button
              onClick={(e) => handleAddClick(e, item, variation)}
              className="text-primary hover:text-primary/80 font-medium"
            >
              Add
            </button>
          </TableCell>
        </TableRow>
        {renderBatchRows(item, variation, variationKey)}
      </Fragment>
    );
  };

  const customTable = (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="w-8" />
          <TableHead>Supplement / LOT</TableHead>
          <TableHead>Order quantity / Initial</TableHead>
          <TableHead>Stock quantity / Current</TableHead>
          <TableHead>Stock (kg) / Exp. date</TableHead>
          <TableHead className="text-right" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {stockItems.map((item) => {
          const isExpanded = expandedItems.has(item.id);
          const hasMultipleVariations = item.stock_info.variations.length > 1;
          const singleVariation = item.stock_info.variations[0];
          const singleVariationKey = `${item.id}-${singleVariation.strength_id}`;

          return (
            <Fragment key={item.id}>
              <TableRow
                className={cn(
                  (hasMultipleVariations ||
                    singleVariation.stocks.length > 0) &&
                    'hover:bg-muted/50 cursor-pointer',
                )}
                onClick={() => {
                  if (hasMultipleVariations) {
                    toggleItemExpand(item.id);
                  } else if (singleVariation.stocks.length > 0) {
                    toggleVariationExpand(singleVariationKey);
                  }
                }}
              >
                <TableCell className="w-8">
                  {(hasMultipleVariations ||
                    singleVariation.stocks.length > 0) && (
                    <ChevronDown
                      className={cn(
                        'size-4 transition-transform',
                        (hasMultipleVariations
                          ? isExpanded
                          : expandedVariations.has(singleVariationKey)) &&
                          'rotate-180',
                      )}
                    />
                  )}
                </TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>
                  {hasMultipleVariations ? (
                    `${item.stock_info.variations.length} variants`
                  ) : (
                    <Tooltip
                      content={singleVariation.strength || ''}
                      enabled={isTruncated(singleVariation.strength || '')}
                    >
                      <span>
                        {truncateDose(singleVariation.strength || '')}
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {hasMultipleVariations
                    ? item.stock_info.total_order_count
                    : singleVariation.order_count}
                </TableCell>
                <TableCell>
                  {hasMultipleVariations
                    ? item.stock_info.total_remaining
                    : singleVariation.remaining}
                </TableCell>
                <TableCell>
                  {hasMultipleVariations
                    ? item.stock_info.total_weight_kg
                    : singleVariation.total_weight_kg}
                </TableCell>
                <TableCell className="text-right">
                  {!hasMultipleVariations && (
                    <button
                      onClick={(e) => handleAddClick(e, item, singleVariation)}
                      className="text-primary hover:text-primary/80 font-medium"
                    >
                      Add
                    </button>
                  )}
                </TableCell>
              </TableRow>

              {hasMultipleVariations &&
                isExpanded &&
                item.stock_info.variations.map((variation) =>
                  renderVariationRow(item, variation),
                )}

              {!hasMultipleVariations &&
                renderBatchRows(item, singleVariation, singleVariationKey)}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );

  return (
    <>
      <DashboardTableAccordion
        value="stock"
        title="Stock"
        accordionOpenValue={accordionOpenValue}
        onOpen={handleAccordionOpen}
        data={stockItems}
        isLoading={isLoading}
        loadingMessage="Loading stock..."
        paginationMeta={meta}
        onPageChange={setPage}
        summarySection={
          <div className="flex flex-col gap-4">
            <StockFilters
              filter={filter}
              onFilterChange={handleFilterChange}
              search={search}
              onSearchChange={handleSearchChange}
            />
          </div>
        }
        customTable={customTable}
      />

      <StockFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        stockRow={selectedRow}
        onSubmit={handleFormSubmit}
        isPending={addMutation.isPending}
      />

      <StockEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        batchRow={selectedBatch}
        onSubmit={handleEditSubmit}
        isPending={updateMutation.isPending}
      />
    </>
  );
};
